import { useClient } from "@/features/Prompt/ClientContext";
import { getAuthorized } from "@/lib/fetch";
import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from "@heroui/react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertCircle,
  Download,
  LucideFileText,
  ZoomIn,
  ZoomOut,
} from "lucide-react";
import { useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import pdfWorkerSrc from "pdfjs-dist/build/pdf.worker.min.mjs?url";
import {
  FactoryMakeAssistantToolUI,
  ToolArguments,
} from "../../../features/Prompt/FactoryMakeAssistantToolUI";

// Configuration for react-pdf
pdfjs.GlobalWorkerOptions.workerSrc = pdfWorkerSrc;

type TextReplaceToolData = {
  date?: string;
  [key: string]: any;
};

/**
 * Hook to retrieve a PDF from the API based on the date
 */
const usePdfByDate = (date?: string, enabled: boolean = false) => {
  const client = useClient();

  return useQuery({
    queryKey: ["pdf", date, client],
    queryFn: async () => {
      if (!date) throw new Error("Date is required");

      // Extract date in YYYYMMDD format from the provided date
      const dateFormatted = date.replace(/-/g, "");

      const response = await getAuthorized(
        `${import.meta.env.VITE_BACKEND_URL}/${client}/pdf/${dateFormatted}`,
      );

      if (!response.ok) {
        throw new Error("PDF introuvable pour cette date");
      }

      const blob = await response.blob();
      return URL.createObjectURL(blob);
    },
    enabled: enabled && !!date,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: false,
  });
};

const PDFModal = ({
  isOpen,
  onClose,
  date,
}: {
  isOpen: boolean;
  onClose: () => void;
  date?: string;
}) => {
  const [numPages, setNumPages] = useState<number>(0);
  const [scale, setScale] = useState<number>(1.25);
  const queryClient = useQueryClient();

  const { data: pdfUrl, isLoading, error } = usePdfByDate(date, isOpen);

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
  };

  const handleZoomIn = () => {
    setScale((prev) => Math.min(prev + 0.25, 3.0));
  };

  const handleZoomOut = () => {
    setScale((prev) => Math.max(prev - 0.25, 0.5));
  };

  const handleDownload = () => {
    if (!pdfUrl || !date) return;

    const link = document.createElement("a");
    link.href = pdfUrl;
    link.download = `${date}-GMI-AN.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleClose = () => {
    if (pdfUrl) {
      URL.revokeObjectURL(pdfUrl);
    }
    // Invalidate the query to force a fresh fetch next time
    queryClient.removeQueries({ queryKey: ["pdf", date] });
    setNumPages(0);
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size="5xl"
      scrollBehavior="inside"
    >
      <ModalContent>
        <ModalHeader className="flex flex-row items-center justify-between">
          <span>Document PDF - {date}</span>
          {pdfUrl && !error && (
            <div className="flex gap-2 mr-5">
              <Button
                isIconOnly
                size="sm"
                variant="light"
                onPress={handleZoomOut}
                isDisabled={scale <= 0.5}
              >
                <ZoomOut className="h-4 w-4" />
              </Button>
              <span className="text-sm self-center min-w-12 text-center">
                {Math.round((scale - 0.25) * 100)}%
              </span>
              <Button
                isIconOnly
                size="sm"
                variant="light"
                onPress={handleZoomIn}
                isDisabled={scale >= 3.0}
              >
                <ZoomIn className="h-4 w-4" />
              </Button>
              <Button
                isIconOnly
                size="sm"
                variant="light"
                onPress={handleDownload}
              >
                <Download className="h-4 w-4" />
              </Button>
            </div>
          )}
        </ModalHeader>
        <ModalBody className="px-0">
          <div className="flex flex-col items-center min-h-96">
            {isLoading && (
              <div className="flex items-center justify-center h-64">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                  <p>Chargement du PDF...</p>
                </div>
              </div>
            )}

            {error && (
              <div className="flex items-center justify-center h-64">
                <div className="text-center text-red-600">
                  <AlertCircle className="h-12 w-12 mx-auto mb-4" />
                  <p>
                    {error instanceof Error ? error.message : "Erreur inconnue"}
                  </p>
                </div>
              </div>
            )}

            {pdfUrl && !error && (
              <Document
                file={pdfUrl}
                onLoadSuccess={onDocumentLoadSuccess}
                className="max-w-full"
                loading={
                  <div className="flex items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  </div>
                }
                error={
                  <div className="flex items-center justify-center h-64 text-red-600">
                    <AlertCircle className="h-12 w-12 mx-auto mb-4" />
                    <p>Erreur lors du chargement du PDF</p>
                  </div>
                }
              >
                {Array.from(new Array(numPages), (_, index) => (
                  <Page
                    key={`page_${index + 1}`}
                    pageNumber={index + 1}
                    width={738 * scale}
                    renderTextLayer={false}
                    renderAnnotationLayer={false}
                    className="mb-4"
                  />
                ))}
              </Document>
            )}
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="danger" variant="light" onPress={handleClose}>
            Fermer
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export const TextReplaceToolComponent = FactoryMakeAssistantToolUI<
  ToolArguments,
  TextReplaceToolData
>({
  toolName: "TextReplaceToolComponent",
  SuccessComponent: ({ result }) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const displayDate = result?.date;

    const handleClick = () => {
      setIsModalOpen(true);
    };

    return (
      <>
        <div
          className="cursor-pointer inline-flex items-center justify-center h-6 px-2 mx-1 rounded-md bg-blue-50 border border-blue-200 text-blue-800 text-xs whitespace-nowrap hover:bg-blue-100 transition-colors my-3"
          onClick={handleClick}
        >
          <LucideFileText className="h-3 w-3 mr-1" />
          <span>Source: {displayDate}</span>
        </div>
        <PDFModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          date={displayDate}
        />
      </>
    );
  },
  LoadingComponent: () => {
    return (
      <div className="cursor-pointer inline-flex items-center justify-center h-6 px-2 mx-1 rounded-md bg-gray-50 border border-gray-200 text-gray-600 text-xs whitespace-nowrap">
        <LucideFileText className="h-3 w-3 mr-1" />
        <span>Loading...</span>
      </div>
    );
  },
});
